import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneCall, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ActivityRow, actionLabel } from "@/components/activity/ActivityRow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { start_date: "", end_date: "", action: "", q: "" };

export default function Activity() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState({ items: [], actions: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const set = (patch) => setFilters({ ...filters, ...patch });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries({ ...f, limit: 500 }).filter(([, v]) => v !== ""));
      const { data } = await axios.get(`${API}/activity`, { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load activity");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(), 250); return () => clearTimeout(t); }, [filters]);

  const active = Object.values(filters).some(Boolean);
  const days = {};
  data.items.forEach((i) => { const d = i.at.slice(0, 10); (days[d] = days[d] || []).push(i); });

  return (
    <div data-testid="activity-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2 flex items-center gap-3"><PhoneCall className="w-8 h-8 text-indigo-600" /> Phone agent &amp; API activity</h1>
          <p className="text-slate-600">Everything done through API keys — Retell bookings, cancellations, statements and invoices sent, and any records created or changed.</p>
        </div>
        <Button variant="outline" onClick={() => load()} disabled={loading} data-testid="activity-refresh"><RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>

      <Card className="bg-white border-0 shadow-lg mb-6" data-testid="activity-filters">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Action</Label>
              <Select value={filters.action || "all"} onValueChange={(v) => set({ action: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="activity-filter-action"><SelectValue placeholder="All actions" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All actions</SelectItem>
                  {data.actions.map((a) => <SelectItem key={a} value={a}>{actionLabel(a)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>From</Label><Input type="date" value={filters.start_date} onChange={(e) => set({ start_date: e.target.value })} data-testid="activity-filter-start" /></div>
            <div><Label>To</Label><Input type="date" value={filters.end_date} onChange={(e) => set({ end_date: e.target.value })} data-testid="activity-filter-end" /></div>
            <div><Label>Search</Label><Input value={filters.q} onChange={(e) => set({ q: e.target.value })} placeholder="customer, key name, text…" data-testid="activity-filter-q" /></div>
          </div>
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-500" data-testid="activity-result-count">{data.total} event{data.total === 1 ? "" : "s"}{data.items.length < data.total ? ` (showing latest ${data.items.length})` : ""}</p>
            {active && <Button variant="ghost" size="sm" onClick={() => setFilters(EMPTY)} data-testid="activity-filters-clear"><X className="w-4 h-4 mr-1" /> Clear filters</Button>}
          </div>
        </CardContent>
      </Card>

      {data.items.length === 0 && !loading ? (
        <Card className="bg-white border-0 shadow-lg"><CardContent className="py-16 text-center text-slate-500" data-testid="activity-empty">No activity matches these filters.</CardContent></Card>
      ) : (
        <div className="space-y-6" data-testid="activity-list">
          {Object.entries(days).map(([d, items]) => (
            <div key={d}>
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">{new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p>
              <Card className="bg-white border-0 shadow"><CardContent className="px-4 divide-y">{items.map((i) => <ActivityRow key={i.id} item={i} />)}</CardContent></Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
